import { useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload } from "lucide-react";

// One uploader for every domain source endpoint (sales, purchases, inventory, withholding). The file's
// own content decides what it is, so the screen asks for nothing but the file; what comes back is shown
// as the importer reported it: the type it recognised, the rows it accepted, and every row it rejected
// or skipped with the reason. A refusal is shown verbatim, because the importer's message says what to fix.
export default function DomainSourceUpload({ id, endpoint, label, accept = ".xlsx,.xls,.csv", onUploaded, testid }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [refusal, setRefusal] = useState("");
  const prefix = testid || endpoint;

  const upload = async () => {
    if (!file) return toast.error("Pilih berkas sumber terlebih dahulu.");
    setBusy(true); setRefusal(""); setResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/engagements/${id}/${endpoint}`, form);
      setResult(Array.isArray(data) ? data : [data]);
      setFile(null);
      toast.success("Sumber terdaftar. Unggah bukan berarti pengujian selesai.");
      onUploaded && onUploaded();
    } catch (e) {
      setRefusal(getErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-3 space-y-2" data-testid={`${prefix}-uploader`}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">{label}</span>
          <Input type="file" accept={accept} className="h-9 w-72 max-w-full" onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid={`${prefix}-upload-file`} />
        </label>
        <Button size="sm" disabled={!file || busy} onClick={upload} data-testid={`${prefix}-upload`}>
          <Upload size={14} className="mr-1" />{busy ? "Mengunggah…" : "Unggah"}
        </Button>
      </div>
      {refusal && <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-sm p-2 whitespace-pre-line" data-testid={`${prefix}-upload-refusal`}>{refusal}</p>}
      {result && result.map((source) => <SourceResult key={source.id} source={source} prefix={prefix} />)}
    </div>
  );
}

function SourceResult({ source, prefix }) {
  const accounting = source.row_accounting;
  const rejected = source.rejected_rows || [];
  const parseErrors = source.parse_errors || [];
  return (
    <div className="border border-border rounded-sm p-2 text-xs space-y-1" data-testid={`${prefix}-upload-result`}>
      <div><span className="font-semibold">{source.source_type}</span>{source.sheet ? ` · sheet ${source.sheet}` : ""} · {source.row_count} baris diterima</div>
      {accounting && (
        <div data-testid={`${prefix}-upload-accounting`}>
          Baris data {accounting.data_rows}: diterima {accounting.accepted}, ditolak {accounting.rejected}, dilewati {accounting.skipped}
          {accounting.reconciles === false && <span className="text-red-800"> — jumlah tidak cocok, laporkan ke tim teknis</span>}
        </div>
      )}
      {rejected.length > 0 && (
        <table className="w-full audit-table text-xs" data-testid={`${prefix}-upload-rejected`}>
          <thead><tr><th>Baris</th><th>Alasan</th><th>Nilai</th></tr></thead>
          <tbody>{rejected.slice(0, 20).map((row, i) => (
            <tr key={i}><td className="tabular-nums">{row.row_no}</td><td>{row.reason || row.reason_code}</td>
              <td>{(row.values || []).join(" · ") || row.invoice_no || row.item_code || "—"}</td></tr>))}
          </tbody>
        </table>
      )}
      {rejected.length > 20 && <div className="text-muted-foreground">…dan {rejected.length - 20} baris ditolak lainnya.</div>}
      {parseErrors.length > 0 && (
        <ul className="list-disc pl-4 text-amber-800" data-testid={`${prefix}-upload-parse-errors`}>
          {parseErrors.map((error, i) => <li key={i}>{error.path}{error.sheet ? ` · ${error.sheet}` : ""}: {error.error}</li>)}
        </ul>
      )}
      {(source.notes || []).length > 0 && <div className="text-muted-foreground">{source.notes.join(" ")}</div>}
    </div>
  );
}
